import { Role } from "types/Role";

/**
 * Mirrors the backend's `SALES_INVOICE_EDIT_PERMISSIONS`
 * (erp-backend `api/services/permissions/sales-invoice-access.js`) — the tier
 * the `canManageSalesInvoices` policy requires on `orders/invoice-settings`,
 * and that `orders/patch`/`orders/post` check inline (only when the billing
 * settings actually change), returning
 * `403 { code: 'INVOICE_SETTINGS_FORBIDDEN' }` otherwise.
 *
 * Also exactly the role list the "customers"/"orders" routes in App.tsx
 * already require to reach the order form (a SalesMan can still prepare a
 * draft's billing settings; a plain company member cannot) — kept here as the
 * one place both the order form and the invoice settings form read it from,
 * instead of each re-listing the roles.
 */
const SALES_INVOICE_EDIT_PERMISSION_GROUPS = [
  "Master",
  "Admin",
  "SalesMan",
  "Signature",
  "Access all customers",
] as const;

export function canManageSalesInvoiceSettings(role?: Role | null): boolean {
  return SALES_INVOICE_EDIT_PERMISSION_GROUPS.some((permissionGroup) =>
    role?.permissionsgroups?.includes(permissionGroup)
  );
}

/**
 * Mirrors the backend's `SALES_INVOICE_CONFIRM_PERMISSIONS`
 * (erp-backend `api/services/permissions/sales-invoice-access.js`) — the tier
 * `invoices/confirm`, `customers/create-invoices-bulk` (mode "build") and the
 * Outgoing-invoices routes require. Deliberately NOT the same list as
 * `canManageSalesInvoiceSettings` above: a SalesMan can prepare a draft (edit
 * tier) but cannot assign it a legal number, render its PDF or email it
 * (confirm tier) — `SALES_INVOICE_EDIT_PERMISSIONS` on the backend is built
 * as `[...CONFIRM, 'SalesMan']` for exactly this reason.
 */
const SALES_INVOICE_CONFIRM_PERMISSION_GROUPS = [
  "Master",
  "Admin",
  "Signature",
  "Access all customers",
] as const;

export function canConfirmSalesInvoices(role?: Role | null): boolean {
  return SALES_INVOICE_CONFIRM_PERMISSION_GROUPS.some((permissionGroup) =>
    role?.permissionsgroups?.includes(permissionGroup)
  );
}
